import { useState } from "react";
import { cn } from "@/lib/utils";
import type { PaaRow } from "./types";

// What the comparison saw on this sheet, under the note that was written from it.
//
// THE PROBLEM THIS SOLVES. The operator signs a document filed with DOB having
// seen one line of prose. To check it they have to open the drawings and hunt —
// and worse, they cannot tell "we understood this sheet and phrased it tersely"
// from "we missed half of what happened".
//
// On 1056 A-100 we knew the exercise room went 1,170 SF to 676 SF and the bike
// room 357 to 195. None of it reached the screen. Meanwhile the note the office
// itself filed for that sheet said the exercise room was ENLARGED — a claim that
// three seconds with these numbers would have killed.
//
// WHY THIS IS PROSE AND NOT A TABLE. The first version listed every record
// change. It was honest, complete, and unreadable: 40 to 75 rows on every sheet
// of a real filing, most of them a label one reading picked up and the other did
// not. A list cannot tell you that thirty of its own rows are two readings of a
// dense sheet disagreeing rather than thirty changes — but a sentence can, and
// the model that just wrote the note is the one thing in the system that knows
// which of the two the sheet is. So the same call writes both, and this renders
// what it said.
//
// WHAT WE ADD TO IT, AND HOW LITTLE. The unexplained-area count is ours: it is
// measured, not judged, and it is the one thing that catches a room that moved
// without changing size. It leads the closed label because it is the only part
// that asks the operator to do something.
//
// Everything else we were tempted to add has been taken back out, twice. A
// measured "the two readings disagreed on 17 entries" line lasted one filing: it
// fired on every sheet, and a number on every row is wallpaper. The generic form
// of the blind spot ("the whole sheet differs") goes the same way whenever the
// summary already said it. THE TEST FOR ANYTHING ON THIS PANEL IS NOT "is it
// true" — it is "does it change what the operator does on THIS sheet". A line
// that appears under all thirty-four rows fails it however true it is.
//
// COLLAPSED BY DEFAULT. Thirty-four sheets of evidence open at once is the
// overwhelming failure this is trying to avoid, so it is one sheet at a time and
// the closed label carries the reason to look.

export function SheetFindings({
  row,
  onCompare,
  onReport,
}: {
  row: PaaRow;
  /** Open the side-by-side viewer — offered right beside the reason you'd want it. */
  onCompare?: () => void;
  /**
   * File a report against this sheet, or revise one already filed. Resolves
   * when it is stored; rejects with a message worth showing if it is not.
   */
  onReport?: (kind: ReportKind, message: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  const summary = row.readSummary?.trim();
  const unexplained = row.unexplainedAreas ?? 0;
  // Nothing to show AND nothing to do — the row stays clean. But once a report
  // can be filed the panel stays, because a sheet we found nothing on is exactly
  // where "we missed a change" gets reported, and hiding the panel there would
  // remove the affordance precisely where it is most needed.
  if (!summary && unexplained === 0 && !row.blindSpot && !onReport) return null;

  // The blind spot earns its line when it is SPECIFIC — n areas differ that
  // nothing readable explains — or when there is no summary to carry it. Its
  // other form ("the whole sheet differs") fires on nearly every sheet of a
  // re-plotted set, and the summary now says the same thing in the model's own
  // words two lines above. Printed both ways it is the boilerplate this design
  // set out not to produce.
  const showBlindSpot = !!row.blindSpot && (unexplained > 0 || !summary);

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[0.78rem] transition-colors",
          unexplained > 0
            ? "font-medium text-ink-soft hover:text-ink"
            : "text-ink-muted hover:text-ink-soft",
        )}
      >
        <span
          aria-hidden
          className={cn("transition-transform text-[0.65rem]", open && "rotate-90")}
        >
          ▶
        </span>
        <Summary unexplained={unexplained} hasSummary={!!summary} />
      </button>

      {open && (
        <div className="mt-1.5 space-y-2.5 rounded-lg border border-rule bg-canvas-2 px-3.5 py-3 text-[0.8rem] leading-relaxed">
          {summary ? (
            <p className="text-ink-soft">{summary}</p>
          ) : (
            <p className="text-ink-muted">
              No summary was written for this sheet. What we measured is below.
            </p>
          )}

          {(showBlindSpot || onCompare) && (
            <div className="flex items-start gap-2 border-t border-rule pt-2.5 text-ink-soft">
              {showBlindSpot && (
                <span aria-hidden className="mt-px shrink-0">
                  👁
                </span>
              )}
              <span className="min-w-0">
                {showBlindSpot && `${row.blindSpot} `}
                {onCompare && (
                  <button
                    type="button"
                    onClick={onCompare}
                    className="font-medium text-ink underline decoration-rule-strong underline-offset-2 hover:decoration-primary"
                  >
                    Compare the drawings
                  </button>
                )}
              </span>
            </div>
          )}

          {onReport && <ReportControl onReport={onReport} />}
        </div>
      )}
    </div>
  );
}

export type ReportKind = "Misdiagnosed" | "Missed";

// "This is wrong" / "You missed something", in her words.
//
// WHY IT IS HERE AND NOT ON THE NOTE. Editing the note is already how a wrong
// note gets fixed, and every edit is recorded — but an edit cannot say WHY. A
// reworded note and a deleted false claim look identical in the log, and they
// call for opposite repairs: better writing, or a reading that was simply wrong.
// The one that prompted this said a jacuzzi had been renamed. It never was. She
// deleted the clause, and the only place that reason existed afterwards was a
// conversation.
//
// AND "MISSED" HAS NO OTHER GESTURE AT ALL. You can delete a wrong sentence;
// there is nothing to edit when the sheet changed and we said nothing.
//
// It sits inside the evidence panel, under the summary and the drawings link,
// because that is where she has just finished disagreeing with us — and it stays
// collapsed until clicked, so a review of thirty-four sheets is not thirty-four
// invitations to complain.
function ReportControl({
  onReport,
}: {
  onReport: (kind: ReportKind, message: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ReportKind>("Misdiagnosed");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  // What she has already sent for this sheet, if anything. Holding the text (not
  // just a flag) is what makes the next send an EDIT rather than a duplicate.
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sent, and not currently being revised: show her what she wrote.
  //
  // SHOWING IT BACK IS THE POINT. A report that disappears the moment it is sent
  // gives her no reason to believe it went anywhere, and no way to fix a
  // half-finished sentence. What is deliberately NOT here is any word about
  // whether the drawings were captured — that is our plumbing, she can do
  // nothing with it, and a line of reassurance on every report is wallpaper.
  if (sent !== null && !open) {
    return (
      <div className="space-y-1.5 border-t border-rule pt-2.5">
        <div className="text-[0.72rem] uppercase tracking-wide text-ink-muted">
          {kind === "Missed" ? "You reported a missed change" : "You reported this as wrong"}
        </div>
        <p className="text-[0.82rem] leading-relaxed text-ink-soft">{sent}</p>
        <button
          type="button"
          onClick={() => {
            setMessage(sent);
            setOpen(true);
          }}
          className="text-[0.78rem] text-ink-muted underline decoration-rule-strong underline-offset-2 hover:text-ink hover:decoration-primary"
        >
          Edit or add more
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="border-t border-rule pt-2.5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[0.78rem] text-ink-muted underline decoration-rule-strong underline-offset-2 hover:text-ink hover:decoration-primary"
        >
          Something here is wrong
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-rule pt-2.5">
      <div className="flex flex-wrap gap-1.5">
        <KindToggle active={kind === "Misdiagnosed"} onClick={() => setKind("Misdiagnosed")}>
          We got this wrong
        </KindToggle>
        <KindToggle active={kind === "Missed"} onClick={() => setKind("Missed")}>
          We missed a change
        </KindToggle>
      </div>

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        autoFocus
        placeholder={
          kind === "Misdiagnosed"
            ? "What did we say that isn't so? (e.g. the jacuzzi was never renamed)"
            : "What changed on this sheet that we didn't mention?"
        }
        className="w-full rounded-lg border border-rule bg-surface px-2.5 py-2 text-[0.82rem] leading-relaxed text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none"
      />

      {error && <p className="text-[0.78rem] text-danger">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={sending || message.trim().length === 0}
          onClick={async () => {
            setSending(true);
            setError(null);
            const text = message.trim();
            try {
              await onReport(kind, text);
              setSent(text);
              setOpen(false);
            } catch (e) {
              // Shown, never swallowed. A report that vanishes quietly teaches
              // her that reporting does nothing.
              setError(e instanceof Error ? e.message : "The report could not be saved.");
            } finally {
              setSending(false);
            }
          }}
          className="rounded-lg bg-primary px-3 py-1.5 text-[0.8rem] font-medium text-white transition-opacity disabled:opacity-40"
        >
          {sending ? "Sending…" : sent === null ? "Send" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
            // Abandoning an edit leaves what was already sent standing, rather
            // than silently reverting the panel to "nothing reported".
            setMessage("");
          }}
          className="text-[0.78rem] text-ink-muted hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function KindToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-lg px-2.5 py-1 text-[0.78rem] font-medium transition-colors",
        active ? "bg-primary-soft text-ink" : "text-ink-muted hover:bg-surface-2 hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

// The closed label. Leads with the unexplained count when there is one, because
// that is the only part that asks the operator to do something.
function Summary({
  unexplained,
  hasSummary,
}: {
  unexplained: number;
  hasSummary: boolean;
}) {
  if (unexplained > 0) {
    return (
      <span>
        {unexplained} {unexplained === 1 ? "area" : "areas"} we couldn’t explain
        {hasSummary && <span className="text-ink-muted"> · what we saw</span>}
      </span>
    );
  }
  return <span>What we saw on this sheet</span>;
}
